import React, { useState, useEffect } from "react";
import { useFormikContext, FieldArray } from "formik";
import { Form, Button, Table, Modal, Badge } from "react-bootstrap";
import { FaTrashAlt } from "react-icons/fa";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DOMPurify from "dompurify";
import TextEditor from "components/TextEditor/TextEditor";
import { Autocomplete, TextField, Box, Tooltip } from "@mui/material";
import { ServiceQuotationFormValues } from "../serviceInitialValues";
import { MecordColors } from "Constants/Color";
import AddButton from "Common/Components/Buttons/AddButton";
import {
  autocompleteDropdownStyles,
  getDisabledInputStyles,
  DETAILS_BG,
  filterPopoverStyles,
  tableStyles,
} from "Constants/Style";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { fetchModelByDropDown } from "slices/Masters/model/thunk";
import { fetchProductsDropDownByModel } from "slices/Masters/product/thunk";
import { fetchGstByDropDown } from "slices/Masters/gst/thunk";
import { fetchSacCodeByDropDown } from "slices/Masters/sac-code/thunk";
import AddShippingAddressModal from "../Components/AddShippingAddressModal";
import NoSelectedProducts from "assets/images/Order/NoSelectedProducts.png";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import AddServiceAccessoriesModal from "../Components/AddServiceAccessoriesModal";
import ViewServiceAccessoriesModal from "../Components/ViewServiceAccessoriesModal";
import { AccessoryItem } from "../Components/ServiceAccessoryRow";

type ServiceFormValues = {
  model: string;
  model_name: string;
  serviceName: string;
  serial_no: string;
  serviceCategory: string;
  serviceMethod: string;
  serviceType: string;
  executionMode: string;
  productQuantity: string;
  serviceRate: string;
  totalServiceAmount: string;
  serviceAddress: any | null; // Full address object
  reportedComplaint: string;
  // Additional fields for payload
  category: string;
  category_name: string;
  sub_category: string;
  sub_category_name: string;
  make: string;
  make_name: string;
  unit: string;
  unit_name: string;
  item: string;
  item_name: string;
  sku: string;
  product_sku: string;
  hsn: string;
  hsn_code: string;
  sac: string;
  sac_code: string;
  gst_percent: number;
  gst_percentage: number;
  product_type: any[];
  prod_description?: string;
  additional_details?: string;
  accessories?: AccessoryItem[];
  [key: string]: any;
};

const commonInputStyle = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "13px",
};

// Validation Schema
const validationSchema = Yup.object().shape({
  model: Yup.string().required("Model is required"),
  serviceName: Yup.string().required("Service is required"),
  serial_no: Yup.string().optional(),
  sac_code: Yup.string().required("SAC Code is required"),
  serviceCategory: Yup.string().required("Service Category is required"),

  serviceMethod: Yup.string().when("serviceCategory", {
    is: (val: string) => ["RMA", "Testing", "Repair"].includes(val),
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("Service Method is required"),
  }),

  serviceType: Yup.string().when("serviceCategory", {
    is: (val: string) =>
      ["RMA", "Testing", "Repair", "Installation", "Others"].includes(val),
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("Service Type is required"),
  }),

  executionMode: Yup.string()
    .when("serviceCategory", {
      is: (val: string) => ["RMA", "Testing"].includes(val),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("Execution Mode is required"),
    }),

  productQuantity: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .required("Product Quantity is required")
    .positive("Quantity must be positive")
    .typeError("Quantity must be a number"),
  serviceRate: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value
    )
    .required("Service Rate is required")
    .min(0, "Rate cannot be negative")
    .typeError("Rate must be a number"),
  serviceAddress: Yup.mixed()
    .optional()
    .nullable()
    .test(
      "is-valid-service-address",
      "Service Address is invalid",
      (value: any) => {
        if (!value) return true;
        // Accept object with shipping_address_id
        if (typeof value === 'object' && value.shipping_address_id) return true;
        // Accept string for backward compatibility
        if (typeof value === 'string' && value.trim() !== '') return true;
        return false;
      }
    ),
});

// Hardcoded dropdown options for services
const serviceCategoryOptions = [
  { id: 1, name: "Calibration" },
  { id: 2, name: "Calibration & Repair" },
  { id: 3, name: "Repair" },
  { id: 4, name: "Testing" },
  { id: 5, name: "Installation" },
  { id: 6, name: "RMA" },
  { id: 7, name: "Others" },
];

const serviceMethodOptions = [
  { id: 1, name: "Offsite" },
  { id: 2, name: "Onsite" },
];

const serviceTypeOptions = [
  { id: 1, name: "Standard" },
  { id: 2, name: "NABL" },
];

const executionModeOptions = [
  { id: 1, name: "Inhouse" },
  { id: 2, name: "3rd party" },
];

const ServiceListSection: React.FC = () => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<ServiceQuotationFormValues>();
  const dispatch = useDispatch<any>();

  const initialFormState: ServiceFormValues = {
    model: "",
    model_name: "",
    serviceName: "",
    serial_no: "",
    serviceCategory: "",
    serviceMethod: "",
    serviceType: "",
    executionMode: "",
    productQuantity: "1",
    serviceRate: "",
    totalServiceAmount: "", // Initial empty
    serviceAddress: null,
    reportedComplaint: "",
    category: "",
    category_name: "",
    sub_category: "",
    sub_category_name: "",
    make: "",
    make_name: "",
    unit: "",
    unit_name: "",
    item: "",
    item_name: "",
    sku: "",
    product_sku: "",
    hsn: "",
    hsn_code: "",
    sac: "",
    sac_code: "",
    gst_percent: 0,
    gst_percentage: 0,
    product_type: [],
    discount_type: "currency",
    discount_value: 0, // raw value expected by backend
    calculated_discount_value: 0, // frontend-only computed amount
    net_value: 0,
    gst_value: 0,
    gross_value: 0,
    prod_description: "",
    additional_details: "",
    accessories: [],
  };

  // Description modal (info icon)
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [descriptionModalContent, setDescriptionModalContent] = useState("");
  const [descriptionModalServiceName, setDescriptionModalServiceName] = useState("");
  // Additional Details modal (pencil icon)
  const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] = useState(false);
  const [additionalDetailsIndex, setAdditionalDetailsIndex] = useState<number | null>(null);
  const [additionalDetailsDraft, setAdditionalDetailsDraft] = useState("");

  const [formData, setFormData] = useState<ServiceFormValues>(initialFormState);
  const [modelOptions, setModelOptions] = useState<any[]>([]);
  const [serviceOptions, setServiceOptions] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [localErrors, setLocalErrors] = useState<{ [key: string]: string }>({});
  const [serviceAddressOptions, setServiceAddressOptions] = useState<any[]>([]);

  // Local state for GST options
  const [gstOptions, setGstOptions] = useState<any[]>([]);
  const [gstLoading, setGstLoading] = useState(false);
  const [sacCodeOptions, setSacCodeOptions] = useState<any[]>([]);
  const [selectedSacCode, setSelectedSacCode] = useState<any>(null);

  // State for shipping address modal
  const [showShippingAddressModal, setShowShippingAddressModal] = useState(false);

  // Accessories are stored on each service row, matching the order-service flow.
  const [showAddAccessoriesModal, setShowAddAccessoriesModal] = useState(false);
  const [showViewAccessoriesModal, setShowViewAccessoriesModal] = useState(false);
  const [accessoriesServiceIndex, setAccessoriesServiceIndex] = useState<number | null>(null);

  /** Open the Add Accessories modal for a specific service row */
  const handleOpenAddAccessories = (index: number) => {
    setAccessoriesServiceIndex(index);
    setShowAddAccessoriesModal(true);
  };

  /** Open the View Accessories modal for a specific service row */
  const handleOpenViewAccessories = (index: number) => {
    setAccessoriesServiceIndex(index);
    setShowViewAccessoriesModal(true);
  };

  /** Store/Update accessories on the selected service row */
  const handleAddAccessories = (accessories: AccessoryItem[]) => {
    if (accessoriesServiceIndex === null) return;

    const updatedServices = values.services.map((item: any, index: number) =>
      index === accessoriesServiceIndex ? { ...item, accessories } : item
    );
    setFieldValue("services", updatedServices);
    toast.success("Accessories saved successfully!");
  };

  /** Delete a service row */
  const handleDeleteServiceWithAccessories = (
    removeFunc: (index: number) => void,
    index: number
  ) => {
    removeFunc(index);
    toast.success("Service deleted successfully!");
  };



  // Handle field changes for service table
  const createHandleFieldChange = (index: number) => (field: string, value: any) => {
    const updatedServices = [...values.services];
    (updatedServices[index] as any)[field] = value;

    // Update both camelCase and snake_case versions for serial number
    if (field === "serial_no" || field === "serialNo" || field === "serial_number") {
      (updatedServices[index] as any).serial_no = value;
      (updatedServices[index] as any).serialNo = value;
      (updatedServices[index] as any).serial_number = value;
    }

    // Update both camelCase and snake_case versions for reported_complaint
    if (field === "reported_complaint" || field === "reportedComplaint") {
      (updatedServices[index] as any).reported_complaint = value;
      (updatedServices[index] as any).reportedComplaint = value;
      (updatedServices[index] as any).reported_complaints = value;
    }

    // Update all quantity field variations when quantity is changed
    if (field === "quantity" || field === "product_quantity" || field === "productQuantity") {
      const qtyValue = typeof value === "string" ? parseFloat(value) || 0 : (value || 0);
      (updatedServices[index] as any).quantity = qtyValue;
      (updatedServices[index] as any).product_quantity = qtyValue;
      (updatedServices[index] as any).productQuantity = qtyValue.toString();
    }

    // Force GST percentage to 0 if customer has Compositional GST type
    if (field === "gst_percentage" && values.company?.gst_type === "Compositional gst") {
      (updatedServices[index] as any)[field] = 0;
    }

    // Helper function to round to 2 decimal places
    const roundToTwoDecimals = (num: number): number => Math.round(num * 100) / 100;

    // Recalculate dependent values
    const unitPrice = (updatedServices[index] as any).unit_price || (updatedServices[index] as any).service_amount || 0;
    const quantity = (updatedServices[index] as any).quantity ?? (updatedServices[index] as any).product_quantity ?? 0;

    // Calculate gross value (simple multiplication) with proper rounding
    const grossValue = roundToTwoDecimals(unitPrice * quantity);

    const discountValueInput = (updatedServices[index] as any).discount_value || 0;
    const discountType = (updatedServices[index] as any).discount_type || "currency";
    const gstPercent = values.company?.gst_type === "Compositional gst"
      ? 0
      : (updatedServices[index] as any).gst_percentage || 0;

    let discountValue = 0;

    // Calculate discount based on type (apply to gross value)
    if (discountType === "percentage" || discountType === "Percentage" || discountType === "%") {
      discountValue = roundToTwoDecimals((grossValue * discountValueInput) / 100);
    } else {
      // Currency discount
      discountValue = roundToTwoDecimals(Math.min(discountValueInput, grossValue));
    }

    const netValue = roundToTwoDecimals(grossValue - discountValue);
    const gstValue = roundToTwoDecimals((netValue * gstPercent) / 100);
    const total = roundToTwoDecimals(netValue + gstValue);

    (updatedServices[index] as any).gross_value = grossValue;
    // IMPORTANT: Preserve the raw discount_value (user input), never overwrite it
    // Only set calculated_discount_value (computed amount for display)
    (updatedServices[index] as any).discount_value = (updatedServices[index] as any).discount_value || 0; // Preserve raw value
    (updatedServices[index] as any).calculated_discount_value = discountValue; // Set computed amount
    (updatedServices[index] as any).net_value = netValue;
    (updatedServices[index] as any).gst_value = gstValue;
    (updatedServices[index] as any).total = total;
    (updatedServices[index] as any).total_price = total;
    (updatedServices[index] as any).total_service_amount = total; // Ensure total_service_amount is updated to final total

    setFieldValue("services", updatedServices);
  };

  // Load Service Address Options based on Company from formik context
  useEffect(() => {
    const company = (values as any).company;
    if (
      company &&
      company.shipping_addresses &&
      Array.isArray(company.shipping_addresses)
    ) {
      const formattedOptions = company.shipping_addresses.map((addr: any) => ({
        id: addr.shipping_address_id,
        shipping_address_id: addr.shipping_address_id,
        name: `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode}`,
        address: addr.address,
        city: addr.city,
        state: addr.state,
        country: addr.country,
        pincode: addr.pincode,
        phone_number: addr.phone_number,
        phone_country: addr.phone_country,
        company_id: addr.company_id || company.company_id,
        is_default: addr.is_default,
        created_at: addr.created_at,
        updated_at: addr.updated_at,
        // Store the full address object for reference
        fullAddress: addr
      }));
      setServiceAddressOptions(formattedOptions);
    } else {
      setServiceAddressOptions([]);
    }
  }, [values]);

  // Calculate service totals when services change (always recalculate to ensure accuracy)
  useEffect(() => {
    if (values.services && values.services.length > 0) {
      const updatedServices = values.services.map((service: any) => {
        const svc = service as any;
        // Always recalculate from base values to ensure accuracy
        const unitPrice = parseFloat(svc.unit_price) || parseFloat(svc.service_amount) || parseFloat(svc.serviceRate) || 0;
        const quantity = parseFloat(svc.quantity) || parseFloat(svc.product_quantity) || parseFloat(svc.productQuantity) || 1;
        const discountValueInput = parseFloat(svc.discount_value) || 0;
        const discountType = svc.discount_type || "currency";
        const gstPercent = values.company?.gst_type === "Compositional gst"
          ? 0
          : (parseFloat(svc.gst_percentage) || parseFloat(svc.gst_percent) || 0);

        // Helper function to round to 2 decimal places
        const roundToTwoDecimals = (num: number): number => Math.round(num * 100) / 100;

        // Calculate gross value (simple multiplication) with proper rounding
        const grossValue = roundToTwoDecimals(unitPrice * quantity);

        let discountValue = 0;
        // Calculate discount based on type (apply to gross value)
        if (discountType === "percentage" || discountType === "Percentage" || discountType === "%") {
          discountValue = roundToTwoDecimals((grossValue * discountValueInput) / 100);
        } else {
          // Currency discount
          discountValue = roundToTwoDecimals(Math.min(discountValueInput, grossValue));
        }

        const netValue = roundToTwoDecimals(grossValue - discountValue);
        const gstValue = roundToTwoDecimals((netValue * gstPercent) / 100);
        const total = roundToTwoDecimals(netValue + gstValue);

        return {
          ...service,
          unit_price: unitPrice,
          quantity: quantity,
          gross_value: grossValue,
          // IMPORTANT: Preserve the raw discount_value (user input), never overwrite it
          // Only set calculated_discount_value (computed amount for display)
          discount_value: svc.discount_value || 0, // Preserve raw value
          calculated_discount_value: discountValue, // Set computed amount
          net_value: netValue,
          gst_value: gstValue,
          total: total,
          total_price: total,
          total_service_amount: total, // Ensure total_service_amount is set to final total
        };
      });

      // Only update if there are changes to prevent infinite loops
      const hasChanges = updatedServices.some((updatedService, index) => {
        const originalService = values.services[index] as any;
        const updatedSvc = updatedService as any;
        return (
          updatedSvc.gross_value !== originalService.gross_value ||
          updatedSvc.calculated_discount_value !== originalService.calculated_discount_value ||
          updatedSvc.net_value !== originalService.net_value ||
          updatedSvc.gst_value !== originalService.gst_value ||
          updatedSvc.total !== originalService.total
        );
      });

      if (hasChanges) {
        setFieldValue("services", updatedServices, false); // Use false to prevent validation loops
      }
    }
    // Note: handleFieldChange already recalculates when individual fields change
    // This effect is mainly for initial load and when services are added/removed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.services.length, values.company?.gst_type]);

  // Effect to handle GST type changes - force GST to 0 for Compositional GST customers
  useEffect(() => {
    if (values.services && values.services.length > 0 && values.company?.gst_type === "Compositional gst") {
      const updatedServices = values.services.map((service: any) => ({
        ...service,
        gst_percentage: 0,
        gst_value: 0,
        total: service.net_value || service.gross_value || service.total || 0
      }));

      setFieldValue("services", updatedServices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.company?.gst_type, values.services.length, setFieldValue]);

  // Fetch Models on Mount
  useEffect(() => {
    dispatch(fetchModelByDropDown())
      .unwrap()
      .then((res: any) => {
        setModelOptions(res?.data || []);
      })
      .catch((err: any) => {
        console.error("Error fetching models:", err);
        toast.error("Failed to fetch models");
      });
  }, [dispatch]);

  // Fetch GST options on component mount
  useEffect(() => {
    const fetchGstOptions = async () => {
      try {
        setGstLoading(true);
        const result = await dispatch(fetchGstByDropDown()).unwrap();
        setGstOptions(result.data || []);
      } catch (error) {
        console.error("Failed to fetch GST options:", error);
        setGstOptions([]);
      } finally {
        setGstLoading(false);
      }
    };

    fetchGstOptions();
  }, [dispatch]);

  // Fetch SAC Code options on component mount
  useEffect(() => {
    dispatch(fetchSacCodeByDropDown())
      .unwrap()
      .then((res: any) => {
        setSacCodeOptions(res?.data || []);
      })
      .catch((err: any) => {
        console.error("Failed to fetch SAC options:", err);
      });
  }, [dispatch]);

  const handleModelSelect = (_: any, selectedModel: any | null) => {
    setSelectedModel(selectedModel);

    if (!selectedModel) {
      handleFormReset();
      return;
    }

    const model_id = selectedModel.model_id;

    setFormData((prev) => ({
      ...prev,
      model: model_id,
      model_name: selectedModel.model_name,
      serviceName: "",
      item: "",
      item_name: "",
    }));

    setSelectedService(null);
    setServiceOptions([]);

    dispatch(fetchProductsDropDownByModel(model_id))
      .unwrap()
      .then((payload: any) => {
        const services = Array.isArray(payload.data) ? payload.data : [];
        setServiceOptions(services);
      })
      .catch(() => toast.error("Error fetching services for selected model"));
  };

  const handleServiceSelect = (_: any, selectedService: any) => {
    setSelectedService(selectedService);

    if (selectedService) {
      setFormData((prev) => ({
        ...prev,
        item:
          selectedService.id ||
          selectedService.product_id ||
          selectedService.item_id ||
          "",
        serviceName:
          selectedService.product_name || selectedService.item_name || "",
        item_name:
          selectedService.product_name || selectedService.item_name || "",
        serviceRate: "",
        totalServiceAmount: "",
        category:
          selectedService.category?.category_id ||
          selectedService.category?.category ||
          "",
        category_name: selectedService.category?.category_name || "",
        sub_category:
          selectedService.sub_category?.sub_category_id ||
          selectedService.sub_category?.sub_category ||
          "",
        sub_category_name:
          selectedService.sub_category?.sub_category_name || "",
        make: selectedService.make?.make_id || "",
        make_name: selectedService.make?.make_name || "",
        model: selectedService.model?.model_id || prev.model,
        model_name: selectedService.model?.model_name || prev.model_name,
        hsn: selectedService.hsn_id || selectedService.hsn?.hsn_code_id || "",
        hsn_code: selectedService.hsn?.hsn_code || "",
        unit: selectedService.unit?.unit_id || selectedService.unit?.unit || "",
        unit_name: selectedService.unit?.unit_name || "",
        sku:
          selectedService.sku ||
          selectedService.item_sku ||
          selectedService.product_sku ||
          "",
        product_sku:
          selectedService.product_sku ||
          selectedService.sku ||
          selectedService.item_sku ||
          "",
        gst_percent:
          selectedService.gst_percent ||
          selectedService.hsn?.gst?.gst_percentage ||
          0,
        gst_percentage:
          values.company?.gst_type === "Compositional gst"
            ? 0
            : (selectedService.hsn?.gst?.gst_percentage ||
              selectedService.gst_percent ||
              0),
        product_type: selectedService.product_types || [],
        prod_description: selectedService.prod_description ?? selectedService.description ?? "",
        additional_details: selectedService.additional_details ?? "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        item: "",
        serviceName: "",
        item_name: "",
        serviceRate: "",
        totalServiceAmount: "",
        category: "",
        category_name: "",
        sub_category: "",
        sub_category_name: "",
        make: "",
        make_name: "",
        hsn: "",
        hsn_code: "",
        unit: "",
        unit_name: "",
        sku: "",
        product_sku: "",
        gst_percent: 0,
        gst_percentage: 0,
        product_type: [],
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Calculate Total Service Amount if Quantity or Rate changes
      if (name === "productQuantity" || name === "serviceRate") {
        const qty =
          parseFloat(
            name === "productQuantity" ? value : prev.productQuantity
          ) || 0;
        const rate =
          parseFloat(name === "serviceRate" ? value : prev.serviceRate) || 0;
        updated.totalServiceAmount = (qty * rate).toFixed(2);
      }

      return updated;
    });

    if (errors[name as keyof typeof errors]) {
      setLocalErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFormReset = () => {
    setFormData(initialFormState);
    setServiceOptions([]);
    setSelectedModel(null);
    setSelectedService(null);
    setSelectedSacCode(null);
    setLocalErrors({});
  };

  // Reset service form when company changes
  useEffect(() => {
    handleFormReset();
  }, [values.company?.company_id]);

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setLocalErrors({});

      // Ensure serviceAddress is the full object, not just a string or partial object
      let serviceAddressToStore = formData.serviceAddress;
      if (serviceAddressToStore) {
        if (typeof serviceAddressToStore === 'string') {
          // If it's a string (formatted address), try to find the full object from company
          const company = (values as any).company;
          if (company?.shipping_addresses?.length > 0) {
            const matchingAddr = company.shipping_addresses.find(
              (addr: any) => {
                const addrString = `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode}`;
                return addrString === serviceAddressToStore || addr.address === serviceAddressToStore;
              }
            );
            if (matchingAddr) {
              serviceAddressToStore = matchingAddr;
            }
          }
        } else if (typeof serviceAddressToStore === 'object' && !serviceAddressToStore.shipping_address_id) {
          // If object is incomplete (missing shipping_address_id), try to get full object from company
          const company = (values as any).company;
          if (company?.shipping_addresses?.length > 0) {
            const matchingAddr = company.shipping_addresses.find(
              (addr: any) => {
                // Try multiple matching strategies
                if (serviceAddressToStore.address) {
                  const addrString = `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode}`;
                  return addrString === serviceAddressToStore.address ||
                    addr.address === serviceAddressToStore.address;
                }
                return false;
              }
            );
            if (matchingAddr) {
              serviceAddressToStore = matchingAddr;
            }
          }
        }
      }

      const payload: any = {
        ...formData,
        type: "service",
        // Ensure model shows the name, not the ID
        model: formData.model_name || formData.model,
        // Map item to product_id for backend
        product_id: formData.item,
        // Ensure serviceAddress is the full object
        serviceAddress: serviceAddressToStore,
      };

      if (!payload.item_name) {
        payload.item_name = formData.serviceName || formData.item_name || "";
      }

      const qty = payload.productQuantity ? parseFloat(payload.productQuantity) : 0;
      const rate = parseFloat(payload.serviceRate) || 0;

      // Calculate gross value (simple multiplication)
      const grossValue = rate * qty;

      // Calculate net value and final total
      const discountValue = 0; // Initial discount is 0
      const netValue = grossValue - discountValue;
      // Get GST percentage from payload (set when service is selected) or form data, accounting for Compositional GST
      const getGstPercent = (value: any): number => {
        if (typeof value === 'number') return isNaN(value) ? 0 : value;
        if (typeof value === 'string') return parseFloat(value) || 0;
        return 0;
      };
      const gstPercentValue = getGstPercent(payload.gst_percentage) || getGstPercent(formData.gst_percentage) || 0;
      const gstPercent = values.company?.gst_type === "Compositional gst" ? 0 : gstPercentValue;
      const gstValue = (netValue * gstPercent) / 100;
      const finalTotal = netValue + gstValue;

      // Tax split logic
      const vals = values as any;
      const qBillFrom = (vals.bill_from_address?.org_state || vals.bill_from_address?.state || "").toString().trim().toLowerCase();
      const qBillTo = (vals.company?.billing_address?.state || vals.billing_address?.state || vals.customer_details?.billing_address?.state || "").toString().trim().toLowerCase();
      const isIntra = !!qBillFrom && !!qBillTo && qBillFrom === qBillTo;

      const cgstPct = values.company?.gst_type === "Compositional gst" ? 0 : isIntra ? Math.round((gstPercent / 2) * 100) / 100 : 0;
      const sgstPct = values.company?.gst_type === "Compositional gst" ? 0 : isIntra ? Math.round((gstPercent / 2) * 100) / 100 : 0;
      const igstPct = values.company?.gst_type === "Compositional gst" ? 0 : !isIntra ? gstPercent : 0;

      // Initialize discount fields
      payload.quantity = qty;
      payload.product_quantity = qty;
      payload.productQuantity = qty.toString();
      payload.unit_price = rate;
      payload.discount_type = "currency";
      payload.gross_value = grossValue;
      payload.discount_value = 0; // raw value expected by backend
      payload.calculated_discount_value = 0; // frontend-only computed amount
      payload.net_value = netValue;
      payload.gst_value = gstValue;
      payload.cgst_percentage = cgstPct;
      payload.sgst_percentage = sgstPct;
      payload.igst_percentage = igstPct;
      payload.total = finalTotal;
      payload.total_price = finalTotal;
      payload.total_service_amount = finalTotal; // Use final total (net + GST), not gross value

      // Ensure reported_complaint is mapped to both camelCase and snake_case
      payload.reported_complaint = formData.reportedComplaint || "";
      payload.reportedComplaint = formData.reportedComplaint || "";
      payload.reported_complaints = formData.reportedComplaint || "";
      payload.prod_description = formData.prod_description ?? "";
      payload.additional_details = formData.additional_details ?? "";
      payload.accessories = [];

      setFieldValue("services", [...values.services, payload]);
      toast.success("Service added successfully!");
      handleFormReset();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path] = error.message;
          }
        });
        setLocalErrors(newErrors);
        toast.error("Please fill all required fields correctly.");
      }
    }
  };

  const handleDelete = (index: number) => {
    const updated = [...values.services];
    updated.splice(index, 1);
    setFieldValue("services", updated);
    toast.success("Service deleted successfully!");
  };

  const handleItemUpdate = (index: number, field: string, value: any) => {
    const updatedItems = [...values.services];
    (updatedItems[index] as any)[field] = value;

    // Recalculate dependent values when service rate changes
    if (field === "unit_price") {
      const unitPrice = value;
      const quantity = (updatedItems[index] as any).quantity || (updatedItems[index] as any).product_quantity || 0;

      // Calculate gross value (simple multiplication)
      const grossValue = unitPrice * quantity;

      // Apply existing discount
      const discountValueInput = (updatedItems[index] as any).discount_value || 0;
      const discountType = (updatedItems[index] as any).discount_type || "currency";
      const gstPercent = values.company?.gst_type === "Compositional gst"
        ? 0
        : (updatedItems[index] as any).gst_percentage || 0;

      let discountValue = 0;
      if (discountType === "percentage") {
        discountValue = (grossValue * discountValueInput) / 100;
      } else {
        discountValue = Math.min(discountValueInput, grossValue);
      }

      const netValue = grossValue - discountValue;
      const gstValue = (netValue * gstPercent) / 100;
      const total = netValue + gstValue;

      const vals = values as any;
      const qBillFrom = (vals.bill_from_address?.org_state || vals.bill_from_address?.state || "").toString().trim().toLowerCase();
      const qBillTo = (vals.company?.billing_address?.state || vals.billing_address?.state || vals.customer_details?.billing_address?.state || "").toString().trim().toLowerCase();
      const isIntra = !!qBillFrom && !!qBillTo && qBillFrom === qBillTo;

      (updatedItems[index] as any).cgst_percentage = values.company?.gst_type === "Compositional gst" ? 0 : isIntra ? Math.round((gstPercent / 2) * 100) / 100 : 0;
      (updatedItems[index] as any).sgst_percentage = values.company?.gst_type === "Compositional gst" ? 0 : isIntra ? Math.round((gstPercent / 2) * 100) / 100 : 0;
      (updatedItems[index] as any).igst_percentage = values.company?.gst_type === "Compositional gst" ? 0 : !isIntra ? gstPercent : 0;

      (updatedItems[index] as any).gross_value = grossValue;
      // IMPORTANT: Preserve the raw discount_value (user input), never overwrite it
      // Only set calculated_discount_value (computed amount for display)
      (updatedItems[index] as any).discount_value = (updatedItems[index] as any).discount_value || 0; // Preserve raw value
      (updatedItems[index] as any).calculated_discount_value = discountValue; // Set computed amount
      (updatedItems[index] as any).net_value = netValue;
      (updatedItems[index] as any).gst_value = gstValue;
      (updatedItems[index] as any).total = total;
      (updatedItems[index] as any).total_price = total;
    }

    setFieldValue("services", updatedItems);
  };

  return (
    <div className="rounded">
      <style>
        {`
          .quotation-description-modal-body {
            max-height: 70vh;
            overflow-x: hidden;
            overflow-y: auto;
          }
          .quotation-description-content {
            max-width: 100%;
            overflow-x: hidden;
            overflow-wrap: anywhere;
            word-break: break-word;
          }
          .quotation-description-content * {
            box-sizing: border-box;
            max-width: 100%;
            overflow-wrap: anywhere;
            word-break: break-word;
            white-space: normal !important;
          }
          .quotation-description-content img,
          .quotation-description-content video,
          .quotation-description-content iframe {
            display: block;
            height: auto;
            max-width: 100%;
          }
          .quotation-description-content table {
            display: block;
            max-width: 100%;
            overflow-x: auto;
          }
          .quotation-description-content h1,
          .quotation-description-content h2,
          .quotation-description-content h3 {
            line-height: 1.2;
          }
        `}
      </style>
      {/* Service List Container */}
      <h5 className="mb-3" style={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
        Services
      </h5>
      <div
        className="p-3 rounded bg-opacity-10 mb-4"
        style={{
          background: MecordColors.ActiveBackground,
        }}
      >
        <Form onSubmit={(e) => e.preventDefault()}>
          {/* Row 1: Model, Service, Serial No */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-3">
              <Form.Group>
                <Form.Label>
                  Model <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={modelOptions}
                  getOptionLabel={(option: any) => option?.model_name || ""}
                  value={selectedModel}
                  onChange={handleModelSelect}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt?.model_name || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Model"
                      variant="outlined"
                      size="small"
                      error={!!localErrors.model}
                      helperText={localErrors.model}
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        ...getDisabledInputStyles(false),
                      }}
                    />
                  )}
                />
              </Form.Group>
            </div>

            <div className="col-12 col-md-3">
              <Form.Group>
                <Form.Label>
                  Product <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={serviceOptions}
                  getOptionLabel={(option: any) => option?.product_name || ""}
                  value={selectedService}
                  onChange={handleServiceSelect}
                  disabled={!selectedModel}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt?.product_name || ""
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Service"
                      variant="outlined"
                      size="small"
                      error={!!localErrors.serviceName}
                      helperText={localErrors.serviceName}
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        ...getDisabledInputStyles(!selectedModel),
                      }}
                    />
                  )}
                />
              </Form.Group>
            </div>

            <div className="col-12 col-md-3">
              <Form.Group>
                <Form.Label>
                  Serial No.
                </Form.Label>
                <Form.Control
                  type="text"
                  name="serial_no"
                  value={formData.serial_no}
                  onChange={handleInputChange}
                  placeholder="Enter Serial No."
                  style={commonInputStyle}
                  className="form-control-placeholder"
                  isInvalid={!!localErrors.serial_no}
                />
                {localErrors.serial_no && (
                  <small className="text-danger">{localErrors.serial_no}</small>
                )}
              </Form.Group>
            </div>

            <div className="col-12 col-md-3">
              <Form.Group>
                <Form.Label style={localErrors.sac_code ? { color: "red" } : {}}>
                  SAC Code <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={sacCodeOptions}
                  getOptionLabel={(option: any) =>
                    typeof option === "string" ? option : option.sac_code || ""
                  }
                  value={selectedSacCode}
                  onChange={(event, newValue: any) => {
                    setSelectedSacCode(newValue);
                    setFormData((prev) => ({
                      ...prev,
                      sac: newValue?.sac_code || "",
                      sac_code: newValue?.sac_code || "",
                    }));
                  }}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => (typeof opt === "string" ? opt : opt.sac_code || "")
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select SAC Code"
                      variant="outlined"
                      size="small"
                      error={!!localErrors.sac_code}
                      helperText={localErrors.sac_code}
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        ...getDisabledInputStyles(false),
                      }}
                    />
                  )}
                />
              </Form.Group>
            </div>
          </div>

          {/* Row 2: Service Category, Method, Type, Execution Mode */}
          {/* Dynamic Field Visibility */}
          {(() => {
            const { serviceCategory, serviceType } = formData;

            const isRmaOrTesting =
              serviceCategory === "RMA" || serviceCategory === "Testing";
            const isRepair = serviceCategory === "Repair";
            const isInstallation = serviceCategory === "Installation";
            const isOther = serviceCategory === "Others";
            const isCalibrationOrRepair =
              serviceCategory === "Calibration" ||
              serviceCategory === "Calibration & Repair";

            // Visibility Rules
            // 1. Calibration: Method (Offsite/Onsite) → Type (Standard/NABL) → Mode (Inhouse or 3rd party)
            // 2. Repair: Mode (Inhouse or 3rd party) - no Method, no Type
            // 3. Calibration & Repair: Same as Calibration
            // 4. Installation: Method (Onsite) → Mode (Inhouse or 3rd party) - no Type
            // 5. Others: Method (Offsite/Onsite) → Mode (Inhouse or 3rd party) - no Type
            // 6. Testing: Hide all
            // 7. RMA: Hide all

            const showMethod = !isRmaOrTesting && !isRepair;
            const showType =
              !isRmaOrTesting && !isRepair && !isInstallation && !isOther;

            // Show mode for all except RMA/Testing
            const showMode = !isRmaOrTesting;

            // Filter Options
            let currentMethodOptions = serviceMethodOptions;
            if (isInstallation) {
              currentMethodOptions = serviceMethodOptions.filter(
                (o) => o.name === "Onsite"
              );
            }

            // All categories show both Inhouse and 3rd party options
            let currentModeOptions = executionModeOptions;

            return (
              <>
                <div className="row g-3 mb-3">
                  {/* Service Category - Always shown */}
                  <div className="col-12 col-md-6 col-lg-3">
                    <Form.Group>
                      <Form.Label>
                        Service Category <span className="text-danger">*</span>
                      </Form.Label>
                      <Autocomplete
                        options={serviceCategoryOptions}
                        getOptionLabel={(option: any) => option.name || ""}
                        value={
                          serviceCategoryOptions.find(
                            (s) => s.name === formData.serviceCategory
                          ) || null
                        }
                        onChange={(event, newValue: any) => {
                          const newCategory = newValue?.name || "";

                          // Reset fields based on new category to avoid invalid states
                          let newMethod = "";
                          let newType = "";
                          let newMode = "";

                          // Logic to pre-set or clear fields based on category
                          if (newCategory === "Installation") {
                            newMethod = "Onsite"; // Force Onsite
                            newType = ""; // Hide Type
                          } else if (newCategory === "Repair") {
                            newMethod = ""; // Hide Method
                            newType = ""; // Hide Type
                            // Don't auto-fill mode - user selects
                          } else if (
                            newCategory === "RMA" ||
                            newCategory === "Testing"
                          ) {
                            newMethod = "NA";
                            newType = "NA";
                            newMode = "NA";
                          } else if (newCategory === "Others") {
                            newType = ""; // Hide Type
                          }

                          setFormData((prev) => ({
                            ...prev,
                            serviceCategory: newCategory,
                            serviceMethod: newMethod,
                            serviceType: newType,
                            executionMode: newMode,
                          }));

                          if (localErrors.serviceCategory) {
                            setLocalErrors((prev) => ({ ...prev, serviceCategory: "" }));
                          }
                        }}
                        ListboxProps={autocompleteDropdownStyles.ListboxProps}
                        renderOption={(props, option) =>
                          autocompleteDropdownStyles.renderOption(
                            props,
                            option,
                            (opt) => opt.name
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Service Category"
                            variant="outlined"
                            size="small"
                            error={!!localErrors.serviceCategory}
                            helperText={localErrors.serviceCategory}
                            sx={{
                              ...autocompleteDropdownStyles.inputSx,
                              ...getDisabledInputStyles(false),
                            }}
                          />
                        )}
                      />
                    </Form.Group>
                  </div>

                  {showMethod && (
                    <div className="col-12 col-md-6 col-lg-3">
                      <Form.Group>
                        <Form.Label>
                          Service Method{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Autocomplete
                          options={currentMethodOptions}
                          getOptionLabel={(option: any) => option.name || ""}
                          value={
                            serviceMethodOptions.find(
                              (s) => s.name === formData.serviceMethod
                            ) || null
                          }
                          onChange={(event, newValue: any) => {
                            setFormData((prev) => ({
                              ...prev,
                              serviceMethod: newValue?.name || "",
                            }));
                            if (localErrors.serviceMethod) {
                              setLocalErrors((prev) => ({
                                ...prev,
                                serviceMethod: "",
                              }));
                            }
                          }}
                          ListboxProps={
                            autocompleteDropdownStyles.ListboxProps
                          }
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) => opt.name
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Method"
                              variant="outlined"
                              size="small"
                              error={!!localErrors.serviceMethod}
                              helperText={localErrors.serviceMethod}
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                ...getDisabledInputStyles(false),
                              }}
                            />
                          )}
                        />
                      </Form.Group>
                    </div>
                  )}

                  {showType && (
                    <div className="col-12 col-md-6 col-lg-3">
                      <Form.Group>
                        <Form.Label>
                          Service Type <span className="text-danger">*</span>
                        </Form.Label>
                        <Autocomplete
                          options={serviceTypeOptions}
                          getOptionLabel={(option: any) => option.name || ""}
                          value={
                            serviceTypeOptions.find(
                              (s) => s.name === formData.serviceType
                            ) || null
                          }
                          onChange={(event, newValue: any) => {
                            const newType = newValue?.name || "";

                            // Don't clear mode when changing type - keep current selection
                            setFormData((prev) => ({
                              ...prev,
                              serviceType: newType,
                              // Keep execution mode as is
                            }));

                            if (localErrors.serviceType) {
                              setLocalErrors((prev) => ({
                                ...prev,
                                serviceType: "",
                              }));
                            }
                          }}
                          ListboxProps={
                            autocompleteDropdownStyles.ListboxProps
                          }
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) => opt.name
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Type"
                              variant="outlined"
                              size="small"
                              error={!!localErrors.serviceType}
                              helperText={localErrors.serviceType}
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                ...getDisabledInputStyles(false),
                              }}
                            />
                          )}
                        />
                      </Form.Group>
                    </div>
                  )}

                  {showMode && (
                    <div className="col-12 col-md-6 col-lg-3">
                      <Form.Group>
                        <Form.Label>
                          Execution Mode{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Autocomplete
                          options={currentModeOptions}
                          getOptionLabel={(option: any) => option.name || ""}
                          value={
                            executionModeOptions.find(
                              (s) => s.name === formData.executionMode
                            ) || null
                          }
                          onChange={(event, newValue: any) => {
                            setFormData((prev) => ({
                              ...prev,
                              executionMode: newValue?.name || "",
                            }));
                            if (localErrors.executionMode) {
                              setLocalErrors((prev) => ({
                                ...prev,
                                executionMode: "",
                              }));
                            }
                          }}
                          ListboxProps={
                            autocompleteDropdownStyles.ListboxProps
                          }
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (opt) => opt.name
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Mode"
                              variant="outlined"
                              size="small"
                              error={!!localErrors.executionMode}
                              helperText={localErrors.executionMode}
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                ...getDisabledInputStyles(false),
                              }}
                            />
                          )}
                        />
                      </Form.Group>
                    </div>
                  )}
                </div>
              </>
            );
          })()}

          {/* Row 3: Product Quantity, Service Rate, Total Service Amount, Service Address */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6 col-lg-3">
              <Form.Group>
                <Form.Label>
                  Product Quantity <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="productQuantity"
                  value={formData.productQuantity}
                  onChange={handleInputChange}
                  placeholder="Enter Quantity"
                  style={commonInputStyle}
                  className="form-control-placeholder"
                  isInvalid={!!localErrors.productQuantity}
                  disabled={true}
                />
                {localErrors.productQuantity && (
                  <small className="text-danger">
                    {localErrors.productQuantity}
                  </small>
                )}
              </Form.Group>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <Form.Group>
                <Form.Label>
                  Service Rate <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="serviceRate"
                  value={formData.serviceRate}
                  onChange={handleInputChange}
                  placeholder="Enter Rate"
                  style={commonInputStyle}
                  className="form-control-placeholder"
                  isInvalid={!!localErrors.serviceRate}
                />
                {localErrors.serviceRate && (
                  <small className="text-danger">{localErrors.serviceRate}</small>
                )}
              </Form.Group>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <Form.Group>
                <Form.Label>
                  Total Amount <span className="text-secondary">(Auto)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="totalServiceAmount"
                  value={formData.totalServiceAmount}
                  readOnly
                  placeholder="0.00"
                  style={{ ...commonInputStyle, backgroundColor: "#e9ecef" }}
                  className="form-control-placeholder"
                />
              </Form.Group>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <Form.Group>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0" style={localErrors.serviceAddress ? { color: "red" } : {}}>
                    Service Address
                  </Form.Label>
                  <span
                    style={{
                      color: MecordColors.ThemeBGColor,
                      fontSize: "0.85rem",
                      cursor: "pointer",
                      fontWeight: "500"
                    }}
                    onClick={() => setShowShippingAddressModal(true)}
                  >
                    <i className="bi bi-plus-circle" style={{ marginRight: "4px" }}></i>
                    Add New
                  </span>
                </div>
                <Autocomplete
                  options={serviceAddressOptions}
                  getOptionLabel={(option: any) => option.name || ""}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option.id === value?.id ||
                    option.shipping_address_id === value?.shipping_address_id
                  }
                  value={
                    (() => {
                      // If serviceAddress is an object, find matching option
                      if (formData.serviceAddress && typeof formData.serviceAddress === 'object') {
                        const addressId = formData.serviceAddress.shipping_address_id || formData.serviceAddress.id;
                        const found = serviceAddressOptions.find(
                          (s) => s.shipping_address_id === addressId || s.id === addressId
                        );
                        if (found) return found;

                        // If not found in options, create a temporary option from the object
                        const addr = formData.serviceAddress;
                        return {
                          id: addr.shipping_address_id || addr.id,
                          shipping_address_id: addr.shipping_address_id || addr.id,
                          name: `${addr.address}, ${addr.city}, ${addr.state}, ${addr.country} - ${addr.pincode}`,
                          address: addr.address,
                          city: addr.city,
                          state: addr.state,
                          country: addr.country,
                          pincode: addr.pincode,
                          phone_number: addr.phone_number,
                          phone_country: addr.phone_country,
                          company_id: addr.company_id,
                          is_default: addr.is_default,
                          created_at: addr.created_at,
                          updated_at: addr.updated_at,
                          fullAddress: addr
                        };
                      }

                      // Legacy support: if serviceAddress is a string, try to match
                      if (typeof formData.serviceAddress === 'string' && formData.serviceAddress) {
                        const found = serviceAddressOptions.find(
                          (s) => s.name === formData.serviceAddress
                        );
                        if (found) return found;
                      }

                      return null;
                    })()
                  }
                  onChange={(event, newValue: any) => {
                    if (newValue) {
                      // Get the full address object from company's shipping_addresses
                      const company = (values as any).company;
                      const addressId = newValue.shipping_address_id || newValue.id;

                      // First try to get from company's shipping_addresses (most reliable)
                      let fullAddress = company?.shipping_addresses?.find(
                        (addr: any) => addr.shipping_address_id === addressId
                      );

                      // If not found, try fullAddress from the option
                      if (!fullAddress && newValue.fullAddress) {
                        fullAddress = newValue.fullAddress;
                      }

                      // If still not found, construct from newValue fields
                      if (!fullAddress) {
                        fullAddress = {
                          shipping_address_id: addressId,
                          company_id: newValue.company_id || company?.company_id,
                          address: newValue.address || "",
                          city: newValue.city || "",
                          state: newValue.state || "",
                          country: newValue.country || "India",
                          pincode: newValue.pincode || "",
                          phone_number: newValue.phone_number || "",
                          phone_country: newValue.phone_country || "+91",
                          is_default: newValue.is_default || false,
                          created_at: newValue.created_at,
                          updated_at: newValue.updated_at
                        };
                      }

                      // Ensure all required fields are present
                      if (!fullAddress.shipping_address_id || !fullAddress.address) {
                        console.warn("Incomplete shipping address object:", fullAddress);
                      }

                      setFormData((prev) => ({
                        ...prev,
                        serviceAddress: fullAddress,
                      }));
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        serviceAddress: null,
                      }));
                    }
                    if (localErrors.serviceAddress) {
                      setLocalErrors((prev) => ({ ...prev, serviceAddress: "" }));
                    }
                  }}
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt) => opt.name
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Address"
                      variant="outlined"
                      size="small"
                      error={!!localErrors.serviceAddress}
                      helperText={localErrors.serviceAddress}
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        ...getDisabledInputStyles(false),
                      }}
                    />
                  )}
                />
              </Form.Group>
            </div>
          </div>

          {/* Row 4: Reported Complaint + Add Button */}
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-9">
              <Form.Group>
                <Form.Label>Reported Complaint</Form.Label>
                <Form.Control
                  as="textarea"
                  name="reportedComplaint"
                  value={formData.reportedComplaint}
                  onChange={handleInputChange}
                  placeholder="Enter Complaint"
                  rows={1}
                  style={commonInputStyle}
                  className="form-control-placeholder"
                />
              </Form.Group>
            </div>

            <div className="col-12 col-md-3 d-flex align-items-end">
              <AddButton
                onClick={handleSubmit}
                disabled={
                  !formData.model ||
                  !formData.serviceName ||
                  !formData.serviceCategory
                }
                label="Add Service"
                height="38px"
                fullWidth={true}
              />
            </div>
          </div>
        </Form>


        {/* Services Table */}
        <FieldArray name="services">
          {({ push, remove }) => (
            <div>
              {values.services.length > 0 ? (
                <div
                  className="table-responsive bg-white rounded"
                  style={{ overflowX: "auto", maxWidth: "100%" }}
                >
                  <Table
                    hover
                    style={{
                      minWidth: "2550px",
                      tableLayout: "fixed",
                      width: "2550px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: MecordColors.LightGrayColor }}
                      >
                        <th style={{ width: "60px", whiteSpace: "nowrap" }}>
                          Sr No.
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Model
                        </th>
                        <th style={{ width: "200px", whiteSpace: "nowrap" }}>
                          Product
                        </th>
                        <th style={{ width: "150px", whiteSpace: "nowrap" }}>
                          Additional Details
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Serial No.
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          SAC Code
                        </th>
                        <th style={{ width: "150px", whiteSpace: "nowrap" }}>
                          Service Category
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Service Method
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Service Type
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Execution Mode
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Product Quantity
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Service Rate
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          Gross Value
                        </th>
                        <th style={{ width: "180px", whiteSpace: "nowrap" }}>
                          Discount/Flat
                        </th>
                        <th style={{ width: "110px", whiteSpace: "nowrap" }}>
                          Discount Value
                        </th>
                        <th style={{ width: "100px", whiteSpace: "nowrap" }}>
                          Net Value
                        </th>
                        <th style={{ width: "120px", whiteSpace: "nowrap" }}>
                          GST (%)
                        </th>
                        {(() => {
                          const vals = values as any;
                          const qBillFrom = (vals.bill_from_address?.org_state || vals.bill_from_address?.state || "").toString().trim().toLowerCase();
                          const qBillTo = (vals.company?.billing_address?.state || vals.billing_address?.state || vals.customer_details?.billing_address?.state || "").toString().trim().toLowerCase();
                          const isIntra = !!qBillFrom && !!qBillTo && qBillFrom === qBillTo;
                          return isIntra ? (
                            <>
                              <th style={{ width: "100px", whiteSpace: "nowrap" }}>SGST (%)</th>
                              <th style={{ width: "100px", whiteSpace: "nowrap" }}>CGST (%)</th>
                            </>
                          ) : (
                            <th style={{ width: "100px", whiteSpace: "nowrap" }}>IGST (%)</th>
                          );
                        })()}
                        <th style={{ width: "100px", whiteSpace: "nowrap" }}>
                          GST Value
                        </th>
                        <th style={{ width: "160px", whiteSpace: "nowrap" }}>
                          Total Service Amount
                        </th>
                        <th style={{ width: "180px", whiteSpace: "nowrap" }}>
                          Total Accessories Amount
                        </th>
                        <th style={{ width: "250px", whiteSpace: "nowrap" }}>
                          Service Address
                        </th>
                        <th style={{ width: "200px", whiteSpace: "nowrap" }}>
                          Reported Complaint
                        </th>
                        <th
                          style={{
                            position: "sticky",
                            right: 0,
                            backgroundColor: MecordColors.LightGrayColor,
                            borderLeft: `1px solid ${MecordColors.LightGrayColor}`,
                            zIndex: 10,
                            width: "80px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody style={{ backgroundColor: "transparent" }}>
                      {values.services.map((item: any, index: number) => {
                        const handleFieldChange = createHandleFieldChange(index);
                        const isLocked =
                          item?.has_challan === true || item?.is_locked === true;

                        return (
                          <tr key={item.id}>
                            <td style={{ width: "60px", textAlign: "center" }}>
                              {index + 1}
                            </td>
                            <td style={{ width: "120px" }}>
                              {item?.model || item?.model_name || "-"}
                            </td>
                            <td style={{ width: "200px", verticalAlign: "middle" }}>
                              <div className="d-flex align-items-center gap-1">
                                <span className="flex-grow-1">
                                  {item?.serviceName || item?.item_name || "-"}
                                </span>
                                <InfoIcon
                                  style={{
                                    cursor: "pointer",
                                    color: tableStyles.primaryColor || MecordColors?.ThemeBGColor,
                                    fontSize: "18px",
                                    flexShrink: 0,
                                  }}
                                  onClick={() => {
                                    const content =
                                      (item as any).prod_description ??
                                      (item as any).description ??
                                      "";
                                    setDescriptionModalContent(content);
                                    setDescriptionModalServiceName(
                                      item?.serviceName || item?.item_name || ""
                                    );
                                    setShowDescriptionModal(true);
                                  }}
                                />
                              </div>
                            </td>
                            <td style={{ width: "60px", textAlign: "center", verticalAlign: "middle" }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <EditIcon
                                  style={{
                                    cursor: isLocked ? "not-allowed" : "pointer",
                                    color: isLocked ? "#6c757d" : (tableStyles.primaryColor || MecordColors?.ThemeBGColor),
                                    fontSize: "20px",
                                  }}
                                  onClick={() => {
                                    if (isLocked) return;
                                    setAdditionalDetailsIndex(index);
                                    setAdditionalDetailsDraft(
                                      (item as any).additional_details ?? ""
                                    );
                                    setShowAdditionalDetailsModal(true);
                                  }}
                                />
                              </div>
                            </td>
                            <td style={{ width: "120px" }}>
                              <Form.Control
                                type="text"
                                disabled={isLocked}
                                value={item?.serial_no || item?.serialNo || ""}
                                onChange={(e) =>
                                  handleFieldChange("serial_no", e.target.value)
                                }
                                placeholder="Serial No."
                                size="sm"
                                style={{
                                  fontSize: "0.85rem",
                                  minHeight: "32px",
                                }}
                              />
                            </td>
                             <td style={{ width: "120px" }}>
                               {item?.sac_code || item?.sac || "-"}
                             </td>
                             <td style={{ width: "150px" }}>
                               {item?.serviceCategory || "-"}
                             </td>
                            <td style={{ width: "120px" }}>
                              {item?.serviceMethod || "-"}
                            </td>
                            <td style={{ width: "120px" }}>
                              {item?.serviceType || "-"}
                            </td>
                            <td style={{ width: "120px" }}>
                              {item?.executionMode || "-"}
                            </td>
                            <td style={{ width: "120px" }}>
                              {item?.quantity ?? item?.product_quantity ?? 0}
                            </td>
                            <td style={{ width: "120px" }}>
                              <div className="d-flex align-items-center">
                                <span className="me-1">₹</span>
                                <Form.Control
                                  type="number"
                                  disabled={isLocked}
                                  min="0.01"
                                  step="0.01"
                                  value={item.unit_price || item.service_amount || 0}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      "unit_price",
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                  size="sm"
                                  style={{ width: "100%" }}
                                />
                              </div>
                            </td>
                            <td style={{ width: "120px" }}>
                              {`${(values as any).currency_symbol || "₹"} ${formatIndianAmount(item.gross_value)}`}
                            </td>
                            <td style={{ width: "180px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Form.Control
                                  type="number"
                                  disabled={isLocked}
                                  size="sm"
                                  value={(item.discount_value ?? 0).toString()}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    const discountType = item.discount_type || "currency";
                                    const grossValue = item.gross_value || 0;

                                    // Validation based on discount type
                                    if (
                                      discountType === "percentage" &&
                                      value > 100
                                    )
                                      return;
                                    if (
                                      discountType === "currency" &&
                                      value > grossValue
                                    )
                                      return;

                                    handleFieldChange("discount_value", value);
                                  }}
                                  min="0"
                                  max={
                                    item.discount_type === "percentage"
                                      ? "100"
                                      : (item.gross_value || 0).toString()
                                  }
                                  style={{ flex: 1 }}
                                />
                                <Form.Select
                                  size="sm"
                                  disabled={isLocked}
                                  value={item.discount_type || "currency"}
                                  onChange={(e) => {
                                    const discountType = e.target.value as "percentage" | "currency";
                                    // Reset discount value when changing type
                                    handleFieldChange("discount_value", 0);
                                    handleFieldChange("discount_type", discountType);
                                  }}
                                  style={{ width: "50px", padding: "4px" }}
                                >
                                  <option value="currency">₹</option>
                                  <option value="percentage">%</option>
                                </Form.Select>
                              </div>
                            </td>
                            <td style={{ width: "110px" }}>
                              {`${(values as any).currency_symbol || "₹"} ${formatIndianAmount(item.calculated_discount_value)}`}
                            </td>
                            <td style={{ width: "100px" }}>
                              {`${(values as any).currency_symbol || "₹"} ${formatIndianAmount(item.net_value)}`}
                            </td>
                            <td style={{ width: "120px" }}>
                              <Autocomplete
                                disableClearable
                                options={gstOptions}
                                getOptionLabel={(option: any) => option.gst_percentage?.toString() || ""}
                                isOptionEqualToValue={(option: any, value: any) =>
                                  option.gst_percentage === value.gst_percentage
                                }
                                value={
                                  gstOptions.find(
                                    (option: any) => option.gst_percentage?.toString() === item.gst_percentage?.toString()
                                  ) || null
                                }
                                onChange={(_, newValue) => {
                                  handleFieldChange(
                                    "gst_percentage",
                                    parseFloat(newValue?.gst_percentage || "0")
                                  );
                                }}
                                disabled={values.company?.gst_type === "Compositional gst" || isLocked}
                                size="small"
                                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                renderOption={(props, option: any, { selected }) => (
                                  <li {...props} key={option.gst_id} style={{ padding: 0 }}>
                                    <Box
                                      sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        width: "100%",
                                        cursor: "pointer",
                                        "&:hover": {
                                          backgroundColor: MecordColors.Hover,
                                          color: MecordColors.ActiveText,
                                        },
                                        ...(selected && {
                                          backgroundColor: MecordColors.ActiveBackground,
                                          color: MecordColors.ActiveText,
                                        }),
                                      }}
                                    >
                                      {option.gst_percentage}%
                                    </Box>
                                  </li>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="GST %"
                                    size="small"
                                    sx={{
                                      ...autocompleteDropdownStyles.inputSx,
                                      '& .MuiInputBase-root': {
                                        minHeight: '31px',
                                        height: '31px',
                                        fontSize: '0.875rem',
                                        padding: '0 8px',
                                      },
                                      '& .MuiInputBase-input': {
                                        padding: '4px 0',
                                        fontSize: '0.875rem',
                                      },
                                    }}
                                  />
                                )}
                              />
                            </td>
                            {(() => {
                              const vals = values as any;
                              const isComp = vals.company?.gst_type === "Compositional gst";
                              const qBillFrom = (vals.bill_from_address?.org_state || vals.bill_from_address?.state || "").toString().trim().toLowerCase();
                              const qBillTo = (vals.company?.billing_address?.state || vals.billing_address?.state || vals.customer_details?.billing_address?.state || "").toString().trim().toLowerCase();
                              const isIntra = !!qBillFrom && !!qBillTo && qBillFrom === qBillTo;
                              const gstPct = isComp ? 0 : Number(item.gst_percentage || item.gst_percent || 0);

                              if (isIntra) {
                                const half = Math.round((gstPct / 2) * 100) / 100;
                                return (
                                  <>
                                    <td style={{ width: "100px", textAlign: "center" }}>
                                      <Form.Control value={isComp ? "0" : half || "-"} disabled size="sm" style={{ width: "100%" }} />
                                    </td>
                                    <td style={{ width: "100px", textAlign: "center" }}>
                                      <Form.Control value={isComp ? "0" : half || "-"} disabled size="sm" style={{ width: "100%" }} />
                                    </td>
                                  </>
                                );
                              } else {
                                return (
                                  <td style={{ width: "100px", textAlign: "center" }}>
                                    <Form.Control value={isComp ? "0" : gstPct || "-"} disabled size="sm" style={{ width: "100%" }} />
                                  </td>
                                );
                              }
                            })()}
                            <td style={{ width: "100px" }}>
                              ₹{Number(item.gst_value || 0).toFixed(2)}
                            </td>
                            <td style={{ width: "160px" }}>
                              <strong>{`${(values as any).currency_symbol || "₹"} ${formatIndianAmount(item.total || item.total_price || item.total_service_amount)}`}</strong>
                            </td>
                            <td style={{ width: "180px", textAlign: "center", verticalAlign: "middle" }}>
                              {(() => {
                                const accessoriesTotal = Array.isArray(item.accessories)
                                  ? item.accessories.reduce(
                                      (sum: number, acc: any) => sum + Number(acc.total_amount || 0),
                                      0
                                    )
                                  : 0;
                                const accessoriesCount = Array.isArray(item.accessories)
                                  ? item.accessories.length
                                  : 0;
                                return (
                                  <div className="d-flex flex-column align-items-center gap-1">
                                    <strong>
                                      {(values as any).currency_symbol || "₹"}{" "}
                                      {formatIndianAmount(accessoriesTotal)}
                                    </strong>
                                    <div className="d-flex align-items-center gap-2">
                                      <InfoIcon
                                        titleAccess={`View Accessories (${accessoriesCount})`}
                                        style={{
                                          cursor: "pointer",
                                          color: tableStyles?.primaryColor || MecordColors?.ThemeBGColor,
                                          fontSize: "18px",
                                        }}
                                        onClick={() => handleOpenViewAccessories(index)}
                                      />
                                      <AddCircleOutlineIcon
                                        titleAccess={isLocked ? "Accessories Locked (Challan Generated)" : "Add / Edit Accessories"}
                                        style={{
                                          cursor: isLocked ? "not-allowed" : "pointer",
                                          color: isLocked ? "#6c757d" : (tableStyles?.primaryColor || MecordColors?.ThemeBGColor),
                                          fontSize: "18px",
                                        }}
                                        onClick={() => {
                                          if (isLocked) {
                                            toast.warning("Accessories cannot be modified because a Challan has already been generated.");
                                            return;
                                          }
                                          handleOpenAddAccessories(index);
                                        }}
                                      />
                                    </div>
                                  </div>
                                );
                              })()}
                            </td>
                            <td style={{ width: "250px" }}>
                              {(() => {
                                const addr = item.serviceAddress || item.service_address;
                                if (addr && typeof addr === 'object') {
                                 const parts = [
                                   addr.address,
                                   addr.city,
                                   addr.state,
                                 ].filter(Boolean);
                                 const pincode = addr.pincode || addr.pin_code;
                                 let result = parts.join(", ");
                                 if (pincode) {
                                   result = result ? `${result} - ${pincode}` : String(pincode);
                                 }
                                 return result || "-";
                                }
                                return typeof addr === 'string' ? addr : "-";
                              })()}
                            </td>
                            <td style={{ width: "200px" }}>
                              <Tooltip
                                title={item.reportedComplaint || item.reported_complaint || ""}
                                arrow
                                placement="top"
                                disableHoverListener={!item.reportedComplaint && !item.reported_complaint}
                              >
                                <div style={{ width: "100%" }}>
                                  <Form.Control
                                    type="textarea"
                                    disabled={isLocked}
                                    value={item.reportedComplaint || item.reported_complaint || ""}
                                    onChange={(e) =>
                                      handleFieldChange(
                                        "reported_complaint",
                                        e.target.value
                                      )
                                    }
                                    size="sm"
                                    style={{ width: "100%" }}
                                    placeholder="Enter complaint"
                                  />
                                </div>
                              </Tooltip>
                            </td>
                            {/* Action Column: Three-dot menu */}
                            <td
                              style={{
                                position: "sticky",
                                right: 0,
                                backgroundColor: "white",
                                borderLeft: `1px solid ${MecordColors.LightGrayColor}`,
                                zIndex: 10,
                                width: "80px",
                                textAlign: "center",
                                verticalAlign: "middle",
                              }}
                            >
                              {isLocked ? (
                                <Badge bg="secondary" style={{ fontSize: "11px" }}>Locked</Badge>
                              ) : (
                                <DynamicMenuItem
                                  row={index}
                                  menuItems={[
                                    {
                                      label: "Delete",
                                      icon: DeleteOutlineIcon,
                                      onClick: (idx: any) => handleDeleteServiceWithAccessories(remove, idx),
                                      color: "error",
                                      textColor: "#dc3545",
                                    },
                                  ]}
                                />
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center text-muted py-4 border rounded p-3 bg-white">
                  <img
                    src={NoSelectedProducts}
                    alt="No services selected"
                    className="mb-3"
                    style={{ maxWidth: "180px", height: "auto" }}
                  />
                  <div>Search existing services to add to this list</div>
                </div>
              )}
            </div>
          )}
        </FieldArray>
        {errors.services && touched.services && (
          <div className="invalid-feedback d-block">{errors.services as string}</div>
        )}

      </div>

      {/* Add Shipping Address Modal */}
      <AddShippingAddressModal
        show={showShippingAddressModal}
        onHide={() => setShowShippingAddressModal(false)}
        companyId={(values as any).company?.company_id}
        formikValues={values}
        setFieldValue={setFieldValue}
        onAddressAdded={(newAddress) => {
          // Auto-select the newly added address - store full object
          if (newAddress) {
            const fullAddress = {
              shipping_address_id: newAddress.shipping_address_id || newAddress.id,
              company_id: newAddress.company_id || (values as any).company?.company_id,
              address: newAddress.address,
              city: newAddress.city,
              state: newAddress.state,
              country: newAddress.country,
              pincode: newAddress.pincode || newAddress.pin_code,
              phone_number: newAddress.phone_number,
              phone_country: newAddress.phone_country || "+91",
              is_default: newAddress.is_default || false,
              created_at: newAddress.created_at,
              updated_at: newAddress.updated_at
            };

            setFormData((prev) => ({
              ...prev,
              serviceAddress: fullAddress,
            }));
          }
        }}
      />

      {/* ─── Accessories Modals ────────────────────────────────────── */}
      {/* Accessories Modals */}
      <AddServiceAccessoriesModal
        show={showAddAccessoriesModal}
        onHide={() => {
          setShowAddAccessoriesModal(false);
          setAccessoriesServiceIndex(null);
        }}
        serviceName={
          accessoriesServiceIndex !== null
            ? (values.services[accessoriesServiceIndex] as any)?.serviceName ||
              (values.services[accessoriesServiceIndex] as any)?.item_name ||
              ""
            : ""
        }
        serviceCategory={
          accessoriesServiceIndex !== null
            ? (values.services[accessoriesServiceIndex] as any)?.serviceCategory || ""
            : ""
        }
        gstOptions={gstOptions}
        existingAccessories={
          accessoriesServiceIndex !== null
            ? (values.services[accessoriesServiceIndex] as any)?.accessories || []
            : []
        }
        onAddAccessories={handleAddAccessories}
        currencySymbol={(values as any).currency_symbol || "\u20b9"}
        isCompositional={values.company?.gst_type === "Compositional gst"}
        isIntraState={(() => {
          const vals = values as any;
          const qBillFromState = (vals.bill_from_address?.org_state || vals.bill_from_address?.state || "").toString().trim().toLowerCase();
          const qBillToState = (vals.company?.billing_address?.state || vals.billing_address?.state || vals.customer_details?.billing_address?.state || "").toString().trim().toLowerCase();
          return !!qBillFromState && !!qBillToState && qBillFromState === qBillToState;
        })()}
      />

      <ViewServiceAccessoriesModal
        show={showViewAccessoriesModal}
        onHide={() => {
          setShowViewAccessoriesModal(false);
          setAccessoriesServiceIndex(null);
        }}
        serviceName={
          accessoriesServiceIndex !== null
            ? (values.services[accessoriesServiceIndex] as any)?.serviceName ||
              (values.services[accessoriesServiceIndex] as any)?.item_name ||
              ""
            : ""
        }
        serviceCategory={
          accessoriesServiceIndex !== null
            ? (values.services[accessoriesServiceIndex] as any)?.serviceCategory || ""
            : ""
        }
        accessories={
          accessoriesServiceIndex !== null
            ? (values.services[accessoriesServiceIndex] as any)?.accessories || []
            : []
        }
        currencySymbol={(values as any).currency_symbol || "\u20b9"}
      />

      {/* Product Description Modal (Info icon) */}
      <Modal
        show={showDescriptionModal}
        onHide={() => {
          setShowDescriptionModal(false);
          setDescriptionModalContent("");
          setDescriptionModalServiceName("");
        }}
        centered
        className="modal-lg"
      >
        <Modal.Header
          closeButton
          style={{
            fontFamily: tableStyles.header.fontFamily,
            fontSize: tableStyles.header.fontSize,
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <Modal.Title
            style={{
              fontFamily: tableStyles.header.fontFamily,
              fontSize: tableStyles.header.fontSize,
              fontWeight: tableStyles.header.fontWeight,
            }}
          >
            Service Description
            {descriptionModalServiceName ? ` - ${descriptionModalServiceName}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="quotation-description-modal-body"
          style={{
            fontFamily: filterPopoverStyles.fontFamily,
            fontSize: filterPopoverStyles.fontSize,
          }}
        >
          {descriptionModalContent ? (
            <div
              className={`${DETAILS_BG} quotation-description-content`}
              style={{
                minHeight: "80px",
                fontFamily: filterPopoverStyles.fontFamily,
                fontSize: filterPopoverStyles.fontSize,
                width: "100%",
                display: "block",
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(descriptionModalContent),
              }}
            />
          ) : (
            <div
              className="text-muted"
              style={{
                fontFamily: filterPopoverStyles.fontFamily,
                fontSize: filterPopoverStyles.fontSize,
              }}
            >
              No description available.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button
            variant="secondary"
            className="rounded"
            style={{
              fontFamily: filterPopoverStyles.button.fontFamily,
              fontSize: filterPopoverStyles.button.fontSize,
              borderColor: filterPopoverStyles.cancelButton.borderColor,
              color: filterPopoverStyles.cancelButton.color,
              backgroundColor: "#fff",
            }}
            onClick={() => {
              setShowDescriptionModal(false);
              setDescriptionModalContent("");
              setDescriptionModalServiceName("");
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Additional Details Modal (Pencil icon) */}
      <Modal
        show={showAdditionalDetailsModal}
        onHide={() => {
          setShowAdditionalDetailsModal(false);
          setAdditionalDetailsIndex(null);
          setAdditionalDetailsDraft("");
        }}
        centered
        className="modal-lg"
      >
        <Modal.Header
          closeButton
          style={{
            fontFamily: tableStyles.header.fontFamily,
            fontSize: tableStyles.header.fontSize,
            borderBottom: "1px solid #dee2e6",
          }}
        >
          <Modal.Title
            style={{
              fontFamily: tableStyles.header.fontFamily,
              fontSize: tableStyles.header.fontSize,
              fontWeight: tableStyles.header.fontWeight,
            }}
          >
            Additional Details
      
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            fontFamily: filterPopoverStyles.fontFamily,
            fontSize: filterPopoverStyles.fontSize,
          }}
        >
          <Form.Group className="mb-0">
            <TextEditor
              value={additionalDetailsDraft}
              onChange={setAdditionalDetailsDraft}
              placeholder="Enter additional details..."
              minHeightPx={120}
              maxHeightPx={200}
              blockImages={true}
            />
          </Form.Group>
          <div className="mb-0 mt-2">
            <hr className="mb-0 w-100" />
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center">
          <Button
            variant="secondary"
            className="px-3 rounded"
            style={{
              fontFamily: filterPopoverStyles.button.fontFamily,
              fontSize: filterPopoverStyles.button.fontSize,
              borderColor: filterPopoverStyles.cancelButton.borderColor,
              color: filterPopoverStyles.cancelButton.color,
              backgroundColor: "#fff",
            }}
            onClick={() => {
              setShowAdditionalDetailsModal(false);
              setAdditionalDetailsIndex(null);
              setAdditionalDetailsDraft("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className="px-3 rounded"
            style={{
              fontFamily: filterPopoverStyles.button.fontFamily,
              fontSize: filterPopoverStyles.button.fontSize,
              backgroundColor: filterPopoverStyles.applyButton.backgroundColor,
              borderColor: filterPopoverStyles.applyButton.backgroundColor,
              color: "#fff",
            }}
            onClick={() => {
              if (additionalDetailsIndex !== null) {
                const updatedServices = [...values.services];
                (updatedServices[additionalDetailsIndex] as any).additional_details =
                  additionalDetailsDraft;
                setFieldValue("services", updatedServices);
              }
              setShowAdditionalDetailsModal(false);
              setAdditionalDetailsIndex(null);
              setAdditionalDetailsDraft("");
            }}
          >
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ServiceListSection;

